import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI, SESSION_TOKEN_KEY } from '../utils/api';

// Storage keys
const AUTH_USER_KEY = 'pokeclass_user';
const AUTH_TIMESTAMP_KEY = 'pokeclass_auth_timestamp';
// Validate the token with the server only ONCE per session window.
// While the cache is valid, navigation (map clicks, cards) never
// re-checks auth against the server.
const AUTH_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const authCheckInProgress = useRef(false);
  const lastAuthCheck = useRef(0);
  // Guard: once a session is established via AuthCallback, block stale verifyAuth results
  const sessionEstablished = useRef(false);

  // Load cached user from localStorage
  // ignoreExpiry=true is used as a survival fallback when the server is
  // momentarily unreachable: keep the student logged in instead of dropping.
  const loadCachedUser = useCallback((ignoreExpiry = false) => {
    try {
      const cachedUser = localStorage.getItem(AUTH_USER_KEY);
      const cachedTimestamp = localStorage.getItem(AUTH_TIMESTAMP_KEY);
      
      if (cachedUser && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp, 10);
        const now = Date.now();
        
        if (ignoreExpiry || now - timestamp < AUTH_CACHE_DURATION) {
          const userData = JSON.parse(cachedUser);
          return userData;
        }
      }
    } catch (e) {
      console.error('[AuthContext] Failed to load cached user:', e);
    }
    return null;
  }, []);

  // Save user to localStorage
  const cacheUser = useCallback((userData) => {
    try {
      if (userData) {
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData));
        localStorage.setItem(AUTH_TIMESTAMP_KEY, Date.now().toString());
      } else {
        localStorage.removeItem(AUTH_USER_KEY);
        localStorage.removeItem(AUTH_TIMESTAMP_KEY);
      }
    } catch (e) {
      console.error('[AuthContext] Failed to cache user:', e);
    }
  }, []);

  // Verify authentication with server
  const verifyAuth = useCallback(async (forceRefresh = false) => {
    // If session was already established by AuthCallback, don't override
    if (sessionEstablished.current && !forceRefresh) {
      console.log('[AuthContext] Session already established, skipping verifyAuth');
      return user;
    }

    // Prevent multiple simultaneous auth checks
    if (authCheckInProgress.current) {
      return user;
    }

    // Rate limit auth checks
    const now = Date.now();
    if (!forceRefresh && now - lastAuthCheck.current < 10000 && user) {
      return user;
    }

    authCheckInProgress.current = true;
    setError(null);

    try {
      // Try cached user first
      if (!forceRefresh) {
        const cachedUser = loadCachedUser();
        if (cachedUser) {
          setUser(cachedUser);
          setIsAuthenticated(true);
          setLoading(false);
          authCheckInProgress.current = false;
          return cachedUser;
        }
      }

      console.log('[AuthContext] Verifying auth with server...');
      const response = await authAPI.me();
      const userData = response.data;
      
      // Check if session was established while we were waiting
      if (sessionEstablished.current && !forceRefresh) {
        console.log('[AuthContext] Session established during async check, ignoring result');
        authCheckInProgress.current = false;
        return user;
      }

      setUser(userData);
      setIsAuthenticated(true);
      cacheUser(userData);
      lastAuthCheck.current = now;
      
      console.log('[AuthContext] Auth verified:', userData.email, 'role:', userData.role);
      return userData;
    } catch (err) {
      // CRITICAL: If session was established by AuthCallback while this was pending, ignore the 401
      if (sessionEstablished.current) {
        console.log('[AuthContext] Ignoring stale 401 - session was established by AuthCallback');
        authCheckInProgress.current = false;
        setLoading(false);
        return user;
      }

      const status = err.response?.status;
      const isAuthRejection = status === 401 || status === 403;

      // Silent reconnection protocol: on transient failures (network drop,
      // timeout, 5xx) DO NOT log the student out. Fall back to the cached
      // user and keep the interface intact.
      if (!isAuthRejection) {
        const fallbackUser = loadCachedUser(true);
        if (fallbackUser) {
          console.warn('[AuthContext] Transient server error — keeping cached session:', status || err.message);
          setUser(fallbackUser);
          setIsAuthenticated(true);
          return fallbackUser;
        }
      }

      console.error('[AuthContext] Auth verification failed:', status || err.message);
      setUser(null);
      setIsAuthenticated(false);
      cacheUser(null);
      try { localStorage.removeItem(SESSION_TOKEN_KEY); } catch (e) { /* noop */ }
      setError(err);
      return null;
    } finally {
      setLoading(false);
      authCheckInProgress.current = false;
    }
  }, [user, loadCachedUser, cacheUser]);

  // Initial auth check on mount
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const href = window.location.href;
    const hash = window.location.hash || '';
    const pathname = window.location.pathname;
    if (href.includes('session_id=') || hash.includes('session_id=') || pathname === '/auth/callback') {
      console.log('[AuthContext] OAuth callback detected, skipping initial auth check');
      setLoading(false);
      return;
    }
    verifyAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update user data (after session exchange, role selection, etc.)
  const updateUser = useCallback((newUserData) => {
    // Mark session as established to block stale verifyAuth results
    if (newUserData) {
      sessionEstablished.current = true;
    }
    setUser(newUserData);
    setIsAuthenticated(!!newUserData);
    cacheUser(newUserData);
    console.log('[AuthContext] User updated:', newUserData?.email);
  }, [cacheUser]);

  // Logout
  const logout = useCallback(async () => {
    sessionEstablished.current = false;
    try {
      await authAPI.logout();
    } catch (e) {
      console.error('[AuthContext] Logout API error:', e);
    }
    setUser(null);
    setIsAuthenticated(false);
    cacheUser(null);
    try { localStorage.removeItem(SESSION_TOKEN_KEY); } catch (e) { /* noop */ }
    lastAuthCheck.current = 0;
  }, [cacheUser]);

  // Set user role
  const setRole = useCallback(async (role) => {
    try {
      await authAPI.setRole(role);
      const updatedUser = { ...user, role };
      updateUser(updatedUser);
      return updatedUser;
    } catch (e) {
      console.error('[AuthContext] Failed to set role:', e);
      throw e;
    }
  }, [user, updateUser]);

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    return verifyAuth(true);
  }, [verifyAuth]);

  const value = {
    user,
    isAuthenticated,
    loading,
    error,
    verifyAuth,
    updateUser,
    logout,
    setRole,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
